import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_BASE } from '../lib/api';
import axios from 'axios';

const AuthContext = createContext(null);

const TOKEN_KEY = 'tcs_access_token';
const USER_KEY = 'tcs_user';

// Set up axios defaults
axios.defaults.withCredentials = true;

// Request interceptor: always attach token from localStorage
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: on 401, clear and redirect (no auto-refresh since token is 7 days)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || '';
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register');
    
    if (error.response?.status === 401 && !isAuthEndpoint) {
      // Token invalid - clear storage
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      // Only redirect if not already on login/register
      const path = window.location.pathname;
      if (path !== '/login' && path !== '/register') {
        window.location.href = '/login';
      }
    }
    
    // 402 Payment Required: subscription overdue — redirect to /payment
    if (error.response?.status === 402) {
      const path = window.location.pathname;
      if (path !== '/payment') {
        window.location.href = '/payment';
      }
    }
    
    return Promise.reject(error);
  }
);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const cachedUser = localStorage.getItem(USER_KEY);
    
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    
    // Set cached user immediately for better UX
    if (cachedUser) {
      try {
        setUser(JSON.parse(cachedUser));
      } catch (e) {
        // ignore
      }
    }
    
    try {
      const { data } = await axios.get(
        `${API_BASE}/api/auth/me`
      );
      setUser(data);
      localStorage.setItem(USER_KEY, JSON.stringify(data));
    } catch (error) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const { data } = await axios.post(
      `${API_BASE}/api/auth/login`,
      { email, password }
    );
    
    if (data.access_token) {
      localStorage.setItem(TOKEN_KEY, data.access_token);
    }
    const userData = { ...data };
    delete userData.access_token;
    delete userData.refresh_token;
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const register = async (payload) => {
    const { data } = await axios.post(
      `${API_BASE}/api/auth/register`,
      payload
    );
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_BASE}/api/auth/logout`);
    } catch (e) {
      // ignore
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(false);
  };

  const updateUser = (newUserData) => {
    const merged = { ...user, ...newUserData };
    localStorage.setItem(USER_KEY, JSON.stringify(merged));
    setUser(merged);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth, setUser, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Algo deu errado. Tente novamente.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};
